/**
 * Build + send a single Solana tx that pays out multiple recipients
 * from the connected treasury wallet. Used by the admin "Pay All
 * Winners" flow for both leaderboard weekly payouts and the monthly
 * jackpot single payout (which reuses this helper with a 1-item list).
 *
 * Tx layout: ComputeBudget (set price + limit) + N × SystemProgram.transfer.
 * At ~70 bytes per transfer + ~450 bytes overhead, 10 transfers fit in
 * ~650 bytes — well inside Solana's 1232-byte tx limit.
 */

import {
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import { sendTx } from "./sendTx";

/**
 * @param {Connection} connection
 * @param {object}     wallet     - wallet-adapter wallet (publicKey + signTransaction)
 * @param {Array<{wallet: string, amount_sol: number}>} payouts
 * @returns {Promise<string>} the confirmed tx signature
 */
export async function sendBatchPayouts(connection, wallet, payouts) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  if (!payouts?.length) throw new Error("No payouts to send");

  const tx = new Transaction();
  for (const p of payouts) {
    const lamports = Math.round(Number(p.amount_sol) * 1e9);
    if (!Number.isFinite(lamports) || lamports <= 0) {
      throw new Error(`Invalid payout amount: ${p.amount_sol} SOL`);
    }
    tx.add(
      SystemProgram.transfer({
        fromPubkey: wallet.publicKey,
        toPubkey: new PublicKey(p.wallet),
        lamports,
      })
    );
  }
  // sendTx adds compute-budget ixs, sets blockhash + fee-payer, signs
  // and confirms with re-broadcast. Returns the on-chain signature.
  const sig = await sendTx(connection, wallet, tx, [], {
    cuLimit: 200_000,
  });
  return sig;
}
